<template>
  <div
      ref="amenitySelect"
      v-bind:class="{expanded: expanded, selectors: !expanded}"
      v-on:click.self="expanded = false"
  >
    <div v-bind:class="{'expanded-content': expanded}">
      <a v-if="!expanded" title="Reset" href="./" class="about no-print">
        <font-awesome-icon icon="redo"/>
      </a>
      <span title="Print" v-if="!expanded" v-on:click="openToPrint" class="about no-print">
                <font-awesome-icon icon="print"/>
            </span>
      <AboutPage class="no-print"/>
      <div v-if="expanded">
        <h2>Transportation Access to Core Services</h2>
        <p class="no-print">How well do transportation systems and land use work together to connect Southeast Michigan
          residents with the places they need or want to go each day? Understanding this relationship helps communities
          prioritize improvements and investments that better serve everyone.
        </p>
        <p class="no-print">This interactive tool allows users to explore transportation access to <strong>core
          services</strong> and <strong>jobs</strong> by <strong>county,
          community, travel mode</strong> and various <strong>demographic groups</strong>. Users can also overlay access
          data with <strong>fixed-route
            transit networks</strong> and <strong>land use context</strong> to better understand the regional framework
          and identify opportunities
          for improvement.</p>
        <p class="no-print">
          Click the
          <font-awesome-icon icon="info"/>
          for more details.
        </p>
        <p class="no-print">Make your selections below and click to see the results.</p>
      </div>
      <div class="group no-print" style="max-width: 350px" v-if="boundaryNames && boundaryNames.length">
        <div class="control-title">1. Choose Community:</div>
        <mcdSelect
            v-bind:mcd="mcd"
            v-bind:counties="counties"
            v-bind:boundaryNames="boundaryNames"
            v-on:mcdInput="setMcd($event)"
            v-on:countiesInput="setCounties($event)"
        />
      </div>
      <div class="group">
        <div class="control-title">2. Choose Access to:</div>
        <div style="border: #f3f3f3 solid 1px; padding: 10px; border-radius: 20px; width: fit-content;">
          <input id="dests" type="radio" v-model="accessType" value="Destination Access"/>
          <label for="dests">Core Services</label>
          <input style="margin-left: 15px" id="jobs" type="radio" v-model="accessType" value="Job Access"/>
          <label for="jobs">Jobs<span class="info"
                                      v-tooltip="{
                        content: `This analysis include the total number of jobs that can be reached within a specific travel time (Cumulative), <br> the analysis also used Gravity method that accounts for the fact that closer jobs are more attractive than distant ones. <br> This analysis does not include a competition-based analysis of access to jobs and does not account for job type or workers’ skills and education. <br> These factors may influence the actual accessibility experienced by different populations and should be considered in future research.`,
                        classes: ['d3-tip'],
                        container: $refs.amenitySelect,
                      }"></span></label>
        </div>
      </div>


      <div class="slidecontainer group">
        <div class="control-title" style="margin-bottom: 20px;">3. Choose Transportation Mode:</div>
        <div style="max-width: 350px">
          <div class="mode-ticks">
            <div v-for="icon in data_tables"
                 v-bind:key="icon"
                 v-bind:title="modeNames[icon].icon"
                 v-on:keyup.enter="$emit('modeInput', icon)"
                 v-bind:class="{'no-print': data_table !== icon}"
                 tabindex="0">
              <font-awesome-icon v-bind:icon="modeNames[icon].icon"/>
              <div class="mode-label">{{ modeNames[icon].name }}</div>
            </div>
          </div>
          <input v-model="selectedModeSlider" type="range" min="1" max="4" value="1" class="slider no-print">
        </div>
        <div
            v-if="data_table === 'bike_comfort_indicators_demographics_by_parcel' || data_table === 'bike_indicators_demographics_by_parcel'">
          <input id='free_flow' v-model="showFreeFlow" type="checkbox"> <label for="free_flow">Not Considering Biking
          Comfort
          Levels <span class="info"
                       v-tooltip="{
                        content: `The bike network used for this output is based on the original OSM road network, with bikeable features.`,
                        classes: ['d3-tip'],
                        container: $refs.amenitySelect,
                      }"></span> </label>
        </div>
      </div>

      <div class="group">
        <div class="no-print" v-if="accessType === 'Job Access'">
          <div class="control-title">4. Choose Type of Access to Jobs:</div>
          <jobSelect
              v-bind:demographicColumns="jobColumns"
              v-bind:demographicColumn="jobColumn"
              v-bind:boundaryNames="boundaryNames"
              v-on:jobInput="setJobs($event)"
          />
        </div>
        <div class="no-print" v-if="accessType === 'Destination Access'">
          <div class="control-title">4. Choose Core Service: <span class="info"
                                                                   v-tooltip="{
                        content: `Please note that the core services listed will vary based on the transportation mode chosen in Step 3.`,
                        classes: ['d3-tip'],
                        container: $refs.amenitySelect,
                      }"></span></div>
          <span> <font-awesome-icon icon="map-marker"
                                    style="color: #B23346; margin-right: 5px"></font-awesome-icon></span>
          <select v-model="selectedAmenity" class="name">
            <option disabled value="">Choose a Core Service</option>
            <option
                v-for="name in amenities"
                v-bind:key="name"
                v-bind:value="name">{{ amenityNames[name] }}
            </option>
          </select>
        </div>

      </div>
      <div class="group">
        <div class="control-title">5. Choose Demographic Layer:</div>
        <div style="border: #f3f3f3 solid 1px; padding: 10px; border-radius: 20px; width: fit-content;">
          <div><input :disabled="bgSummaryLayerOn" id="access" type="radio" v-model="demographicType" value="access"/>
            <label for="access">View Demographic Data by Access Time <span class="info"
                                                                           v-tooltip="{
                        content: `Get visualization and summary of selected demographic data by access travel time for the selected community.`,
                        classes: ['d3-tip'],
                        container: $refs.amenitySelect,
                      }"></span></label></div>
          <div><input :disabled="bgSummaryLayerOn" id="overlay" type="radio" v-model="demographicType" value="overlay"/>
            <label for="overlay">View Additional Demographic Data <span class="info"
                                                                        v-tooltip="{
                        content: `Overly selected demographic data over access travel time.`,
                        classes: ['d3-tip'],
                        container: $refs.amenitySelect,
                      }"></span></label></div>
          <div class="no-print" v-if="demographicType === 'access'">
            <div class="control-title">Demographic Data by Access Time:</div>
            <demographicSelect
                :avgTravel="bgSummaryLayerOn"
                v-bind:demographicColumns="demographicColumns"
                v-bind:demographicColumn="demographicColumn"
                v-bind:boundaryNames="boundaryNames"
                v-on:demographicInput="setDemographic($event)"
            />
          </div>
          <div class="no-print" v-if="demographicType === 'overlay'">
            <div class="control-title">Additional Demographic Data:</div>
            <demographicSelect
                :avgTravel="bgSummaryLayerOn"
                v-bind:demographicColumns="dotDensDemographicColumns"
                v-bind:demographicColumn="dotDensDemographicColumn"
                v-bind:boundaryNames="boundaryNames"
                v-on:demographicInput="setdotDensDemographic($event)"
            />
          </div>
        </div>

      </div>

      <div class="no-print group">
        <div
            v-if="accessType === 'Destination Access'">
          <div style="display: grid; grid-template-columns: auto 1fr; align-items: center;">
            <input style="width: 20px; height: 20px;" id='avgTravel' v-model="showAvgTravel"
                   type="checkbox">
            <label style="font-size: medium; font-weight: bold; max-width: 340px;  line-height: .8em" for="avgTravel">
              Show Average {{ modeNames[data_table].name }} Time by Transportation Access Focus Population
              <span class="info"
                    v-tooltip="{
                        content: 'Vulnerable populations in need of transit services as identified by a composite score from eight demographic groups <br>\(transit-dependent households, households in poverty, households with seniors, households with children, population with disability, <br> households with persons 14 years and older who have limited English ability, minority-headed households, and female-headed households) <br> relative to the regional average.',
                        classes: ['d3-tip'],
                        container: $refs.amenitySelect,
                      }"></span></label>
          </div>
        </div>
        <div style="margin-top: 15px; font-size: x-small"> The access area layer is showing an estimate, the layer may shift when
          zooming.
        </div>
      </div>
      <button class="result-button no-print" v-if="expanded" v-on:click="expanded = !expanded">See Results</button>
    </div>
  </div>
</template>

<script>
import mcdSelect from "./mcdSelect";
import demographicSelect from "./demographicSelect";
import jobSelect from "./jobSelect";
import AboutPage from "./AboutPage";

export default {
  name: "AmenitySelect",
  components: {mcdSelect, demographicSelect, AboutPage, jobSelect},
  props: ['mappedAmenity', 'amenities', 'amenityNames', 'boundaryNames', 'counties', 'mcd', 'data_tables', 'data_table', 'modeNames', 'demographicColumn', 'demographicColumns', 'dotDensDemographicColumn', 'dotDensDemographicColumns', 'startExpanded', 'jobColumns', 'jobColumn', 'jobColumnNames', 'bgSummaryLayerOn', 'freeFlow'],
  data: function () {
    return {
      selectedAmenity: this.mappedAmenity,
      selectedModeSlider: this.data_tables.findIndex((element) => element === this.data_table) + 1,
      selectedDemographic: this.demographicColumn,
      showFreeFlow: this.freeFlow || false,
      showAvgTravel: this.bgSummaryLayerOn || false,
      expanded: this.startExpanded,
      allowZoom: true,
      accessType: 'Destination Access',
      demographicType: 'access',
    }
  },
  methods: {
    setMcd: function (id) {
      this.$emit('mcdInput', id)
    },
    setCounties: function (id) {
      this.$emit('countiesInput', id)
    },
    setDemographic: function (id) {
      this.$emit('demographicInput', id);
    },
    setdotDensDemographic: function (id) {
      this.$emit('dotDensDemographicInput', id);
    },
    setJobs: function (id) {
      this.$emit('jobInput', id);
    },
    openToPrint: function () {
      let search = window.location.search || '?';
      let counties = this.counties.map(c => '&county=' + c.statevalue).join('');
      window.open(
          window.location.pathname + search + '&print=true' + counties,
          "",
          "width=1000, height=700, dependent=true"
      );
    }
  },
  watch: {
    mappedAmenity: function () {
      this.selectedAmenity = this.mappedAmenity
    },
    data_table: function () {
      this.selectedModeSlider = this.data_tables.findIndex((element) => element === this.data_table) + 1
      if (this.accessType === 'Destination Access') {
        if (!this.amenities.includes(this.selectedAmenity)) {
          this.selectedAmenity = this.amenities[0]
        }
      } else if (this.jobColumn && this.jobColumnNames[this.jobColumn]) {
        this.$emit('jobInput', this.jobColumn);
      } else {
        this.$emit('jobInput', this.jobColumns[1].value);
      }
    },
    selectedAmenity: function () {
      this.$emit('input', this.selectedAmenity)
    },
    selectedModeSlider: function () {
      this.$emit('modeInput', this.data_tables[this.selectedModeSlider - 1])
    },
    allowZoom: function () {
      this.$emit('allowZoom', this.allowZoom)
    },
    showFreeFlow: function () {
      this.$emit('freeFlow', this.showFreeFlow)
    },
    showAvgTravel: function () {
      this.$emit('avgTravel', this.showAvgTravel)
    },
    expanded: function () {
      this.$emit('expandedInput', this.expanded)
    },
    accessType: function () {
      if (this.accessType === 'Destination Access') {
        this.$emit('jobInput', undefined);
      } else if (this.jobColumn && this.jobColumnNames[this.jobColumn]) {
        this.$emit('jobInput', this.jobColumn);
      } else {
        this.$emit('jobInput', this.jobColumns[1].value);
      }
    },
    demographicType: function () {
      if (this.demographicType === 'access') {
        this.setdotDensDemographic(null)
      } else {
        this.setDemographic(null)
      }
    },
    jobColumn: {
      immediate: true,
      handler: function () {
        if (this.jobColumn) {
          this.accessType = 'Job Access'
        } else {
          this.accessType = 'Destination Access'
        }
      }
    }
  }
}
</script>

<style scoped>
select {
  border-color: #f3f3f3;
}

select:focus {
  outline: 0;
  border-color: black;
}

select {
  border-radius: 10px;
  min-width: 0;
}

select option {
  font-size: 1rem;
}

.about {
  float: right;
  padding: 5px;
  color: black;
}

@media screen {
  div.expanded {
    position: fixed;
    background: rgba(46, 60, 67, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3;
    opacity: 1;
    overflow: hidden;
    backdrop-filter: blur(35px);
  }

  .expanded-content {
    background: white;
    max-width: 60%;
    border: #fffcfc solid 2px;
    border-radius: 10px;
    padding: 10px 30px;
    max-height: 80%;
    overflow: auto;
  }
}

.control-title {
  font-weight: bold;
  font-size: large;
}

.slidecontainer {
  width: 100%;
}

.mode-ticks {
  display: grid;
  width: 100%;
  grid-template-columns: min-content min-content min-content min-content;
  justify-content: space-between;
  margin-bottom: -45px;
  padding: 0 calc(29px - .7em);
}

@media print {
  .slidecontainer > div {
    display: inline-block;
  }

  .mode-ticks {
    margin-bottom: 0;
    display: inline-block;
  }
}

.mode-ticks div {
  text-align: center;
  justify-self: center;
}

.slider {
  -webkit-appearance: none;
  width: 100%;
  height: 50px;
  border-radius: 20px;
  background: #d3d3d3;
  outline: none;
  opacity: 0.5;
  -webkit-transition: .2s;
  transition: opacity .2s;
}

.slider:hover {
  opacity: 0.3;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 58px;
  height: 65px;
  border-radius: 20px;
  background: #64983D;
  cursor: pointer;
}

.slider::-moz-range-thumb {
  width: 58px;
  height: 65px;
  border-radius: 20px;
  background: #64983D;
  cursor: pointer;
}

.amenityIcon {
  height: 35px;
  width: 35px;
  vertical-align: bottom;
  margin-left: 7px;
}

.mode-label {
  font-size: 0.5em
}

.result-button {
  float: right;
  height: 30px;
  background: rgba(100, 152, 61, 0.75);
  border-color: rgba(100, 152, 61, 0.75);
}

.group {
  padding: 5px;
}

@media print {
  .no-print {
    display: none;
  }
}

@media screen and (max-width: 576px) {
  .expanded-content {
    position: relative;
  }

  div.expanded {
    z-index: 33;
  }
}

@media screen and (max-width: 1000px) {
  .control-title {
    font-size: small;
    display: inline-block;
  }

  .expanded-content {
    max-width: 80%;
    max-height: 100%;
  }

}

.info {
  content: '';
  cursor: auto;
  position: relative;
  display: inline-block;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E %3Cpath fill='%239f9f9f' fill-rule='evenodd' d='M4,10a6,6 0 1,0 12,0a6,6 0 1,0 -12,0 M9,7a1,1 0 1,0 2,0a1,1 0 1,0 -2,0 M9,10a1,1 0 1,1 2,0l0,3a1,1 0 1,1 -2,0'/%3E %3C/svg%3E");
  background-color: rgba(255, 255, 255, 0.5);
  width: 24px;
  height: 24px;
  box-sizing: border-box;
  border-radius: 12px;
}


</style>
